<template>
  <article
    class="gh-card gh-card--design"
    :class="{
      'gh-card--collapsed': collapsed,
      'gh-card--design-assist': isKeyVisualAssist,
    }"
  >
    <slot name="context-head" />
    <div class="gh-card-design__row">
      <button type="button" class="gh-card__chevron" @click="$emit('toggle-collapse')">
        <i
          :class="collapsed ? 'fa fa-chevron-up' : 'fa fa-chevron-down' "
          aria-hidden="true"
        ></i>
      </button>
      <div class="gh-card-design__image">
        <img
          v-if="card.output.url"
          ref="cardImage"
          :src="card.output.url"
          class="gh-card__image"
          :class="{ 'gh-dnd-source': canDrag }"
          alt=""
          :draggable="canDrag"
          @dragstart="onImageDragStart"
          @dragend="onDragEnd"
        >
      </div>
      <div class="gh-card-design__prompt-col">
        <GenerationHistoryCardSlide :expanded="!collapsed">
          <div class="gh-card-design__prompt">
            <template v-if="isKeyVisualAssist">
              <div class="gh-card-text__meta gh-card-text__meta--in-design">
                <GenerationHistoryMetaRows
                  :fields="assistMetaFields"
                  variant="detail"
                  :prompt-tooltip="true"
                  :can-drag-fields="canDrag"
                  :row-key-prefix="`design-assist-${card.item_id}`"
                  @field-drag-start="onAssistMetaDragStart"
                  @field-drag-end="onDragEnd"
                />
              </div>
            </template>
            <template v-else>
              <div class="gh-card-design__prompt-top">
                <span class="gh-card-design__field-label">{{ $t('generation_history.meta.prompt') }}</span>
                <div
                  class="gh-card-design__field-value gh-card-design__field-value--prompt"
                  :class="{ 'gh-dnd-source': canDrag && promptText }"
                  :draggable="canDrag && !!promptText"
                  @dragstart="onPromptDragStart"
                  @dragend="onDragEnd"
                >
                  <GenerationHistoryPromptPopover
                    :text="card.input_meta.prompt"
                    tooltip
                  />
                </div>
              </div>
              <div class="gh-card-design__prompt-bottom">
                <span class="gh-card-design__field-label">{{ $t('generation_history.meta.ai_model') }}</span>
                <span class="gh-card-design__field-value">{{ aiModelLabel }}</span>
              </div>
            </template>
          </div>
        </GenerationHistoryCardSlide>
      </div>
      <div class="gh-card-design__actions-col">
        <GenerationHistoryDesignCardActions
          :is-favorite="card.is_favorite"
          :is-image-copied="isImageCopied"
          :is-prompt-copied="isPromptCopied || localPromptCopied"
          @copy-image="$emit('copy-image', $refs.cardImage)"
          @copy-prompt="onCopyPromptClick"
          @download="$emit('download')"
          @favorite="$emit('favorite')"
        />
        <GenerationHistoryCardSlide :expanded="!collapsed">
          <div class="gh-card-design__size">
            <span class="gh-card-design__field-label">{{ $t('generation_history.meta.image_size') }}</span>
            <span class="gh-card-design__field-value">({{ imageSizeLabel }})</span>
          </div>
        </GenerationHistoryCardSlide>
      </div>
    </div>
  </article>
</template>

<script>
import { formatAiModelDisplay, formatDesignImageSizeLabel, isKeyVisualAssistMeta, getDesignCopyPromptText, copyTextToClipboard } from '@/shared/helpers/generationHistory';
import { GH_DRAG_SOURCE } from '@/shared/helpers/generationHistoryDrag';
import { GH_TEXT_FIELD } from '@/shared/helpers/generationHistoryAssistDrag';
import constants from '@/shared/constants/constants';
import { generationHistoryCardDragMixin } from '@/shared/mixins/generationHistoryDragDropMixin';
import GenerationHistoryCardSlide from '../shared/GenerationHistoryCardSlide.vue';
import GenerationHistoryDesignCardActions from '../shared/GenerationHistoryDesignCardActions.vue';
import GenerationHistoryMetaRows from '../shared/GenerationHistoryMetaRows.vue';
import GenerationHistoryPromptPopover from '../shared/GenerationHistoryPromptPopover.vue';

const ASSIST_FIELD_DEFS = [
  { key: GH_TEXT_FIELD.CATEGORY_SHAPE, labelKey: 'generation_history.meta.category_shape' },
  { key: GH_TEXT_FIELD.CONCEPT, labelKey: 'generation_history.meta.concept' },
  { key: GH_TEXT_FIELD.BRAND, labelKey: 'generation_history.meta.brand' },
  { key: GH_TEXT_FIELD.COMPANY, labelKey: 'generation_history.meta.company' },
];

export default {
  name: 'GenerationHistoryDetailCardDesign',
  mixins: [generationHistoryCardDragMixin],
  components: {
    GenerationHistoryCardSlide,
    GenerationHistoryDesignCardActions,
    GenerationHistoryMetaRows,
    GenerationHistoryPromptPopover,
  },
  props: {
    card: {
      type: Object,
      required: true,
    },
    projectType: {
      type: Number,
      required: true,
    },
    cardIndex: {
      type: Number,
      default: 0,
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
    isImageCopied: {
      type: Boolean,
      default: false,
    },
    isPromptCopied: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localPromptCopied: false,
      localPromptCopiedTimer: null,
    };
  },
  computed: {
    inputMeta() {
      return this.card.input_meta || {};
    },
    isKeyVisualAssist() {
      return isKeyVisualAssistMeta(this.inputMeta);
    },
    promptText() {
      return this.inputMeta.prompt || '';
    },
    assistMetaFields() {
      const fields = ASSIST_FIELD_DEFS
        .map((field) => ({
          ...field,
          value: this.inputMeta[field.key] || '',
        }))
        .filter((field) => field.value);
      if (this.aiModelLabel) {
        fields.push({
          key: 'ai_model',
          labelKey: 'generation_history.meta.ai_model',
          value: this.aiModelLabel,
          draggable: false,
        });
      }
      return fields;
    },
    aiModelLabel() {
      return formatAiModelDisplay(
        this.inputMeta.ai_model,
        this.projectType,
        this.inputMeta,
      );
    },
    imageSizeLabel() {
      return formatDesignImageSizeLabel(this.inputMeta, (key) => this.$t(key));
    },
  },
  beforeDestroy() {
    if (this.localPromptCopiedTimer) {
      clearTimeout(this.localPromptCopiedTimer);
    }
  },
  methods: {
    async onCopyPromptClick() {
      try {
        await copyTextToClipboard(getDesignCopyPromptText(this.card));
        this.localPromptCopied = true;
        if (this.localPromptCopiedTimer) {
          clearTimeout(this.localPromptCopiedTimer);
        }
        this.localPromptCopiedTimer = setTimeout(() => {
          this.localPromptCopied = false;
          this.localPromptCopiedTimer = null;
        }, 3000);
      } catch (e) {
        alert(this.$t('generation_history.processing_failed_message'));
      }
    },
    onImageDragStart(event) {
      this.startGhImageDrag(event, {
        url: this.card.output && this.card.output.url,
        itemType: constants.GENERATION_HISTORY_FAVORITE_ITEM_DESIGN,
        posterIndex: 0,
        sourceType: GH_DRAG_SOURCE.DESIGN,
      });
    },
    onPromptDragStart(event) {
      this.startGhTextDrag(event, {
        text: this.promptText,
        sourceType: GH_DRAG_SOURCE.DESIGN,
        field: GH_TEXT_FIELD.PROMPT,
      });
    },
    onAssistMetaDragStart(event, field) {
      if (!field || field.key === 'ai_model') {
        event.preventDefault();
        return;
      }
      this.startGhTextDrag(event, {
        text: field.value,
        sourceType: GH_DRAG_SOURCE.DESIGN,
        field: field.key,
      });
    },
  },
};
</script>
